import PortalSidebar from "../PortalSidebar/PortalSidebar";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import loadimage from "../../LoadImage.png";

/* ────────── tiny paginated footer ────────── */
const Pagination = ({ page, total, perPage, onChange }) => {
  const first = (page - 1) * perPage + 1;
  const last = Math.min(first + perPage - 1, total);
  const pages = Math.ceil(total / perPage);
  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        Previous
      </button>
      <span className={styles.pageMeta}>
        {first}-{last} of {total}
      </span>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
      >
        Next
      </button>
    </div>
  );
};

function transformNoteHtml(htmlString) {
  if (!htmlString) return "";
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  doc.querySelectorAll("a").forEach((a) => {
    const txt = (a.textContent || "").trim();
    if (/\.(eml|msg)$/i.test(txt)) {
      const span = doc.createElement("span");
      span.textContent = txt;
      span.className = a.className;
      a.replaceWith(span);
    }
  });

  return doc.body.innerHTML;
}

export default function LegalDetailPage() {
  const { id, buildingId } = useParams();
  const navigate = useNavigate();
  const buildingname = localStorage.getItem("buildingName");
  const [legal, setLegal] = useState(null);
  const [payments, setPayments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [aodMeta, setAodMeta] = useState({ exists: false, file_name: null });
  const [dropdownVisible, setDropdownVisible] = useState(false);

  // ── NEW: timeline-note input & posting state
  const [newTimelineNote, setNewTimelineNote] = useState("");
  const [postingNote, setPostingNote] = useState(false);

  /* ────────── helpers ────────── */
  const display = (obj, key) =>
    obj?.[`${key}@OData.Community.Display.V1.FormattedValue`] ??
    obj?.[key] ??
    "—";

  const fmtDate = (d) => {
    try {
      return format(new Date(d), "dd MMM yyyy HH:mm");
    } catch {
      return d ?? "—";
    }
  };

  const formatAmount = (n) => {
    if (n == null || isNaN(n)) return "—";
    const [int, dec] = Number(n).toFixed(2).split(".");
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, " ") + "," + dec;
  };

  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);


  /* ────────── fetch everything ────────── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/");

    (async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [leg, pay, nt] = await Promise.all([
          fetch(`https://portal.gmacc.co.za/api/legals/${id}/`, { headers }),
          fetch(
            `https://portal.gmacc.co.za/api/legals/${id}/payments/${buildingId}`,
            { headers }
          ),
          fetch(`https://portal.gmacc.co.za/api/legals/${id}/timeline/`, {
            headers,
          }),
        ]);
        if (!leg.ok || !pay.ok || !nt.ok) throw new Error();

        const legJs = await leg.json();
        setLegal(legJs);

        try {
          const metaRes = await fetch(
            `https://portal.gmacc.co.za/api/legal/${id}/aod-file/meta/`,
            { headers }
          );
          if (metaRes.ok) {
            const meta = await metaRes.json();
            setAodMeta({
              exists: !!meta.exists,
              file_name: meta.file_name || null,
            });
          } else {
            setAodMeta({ exists: false, file_name: null });
          }
        } catch {
          setAodMeta({ exists: false, file_name: null });
        }

        const payJs = await pay.json();
        const sortedPay = [...(payJs.value || [])].sort((a, b) =>
          b.cr446_paymentpk.localeCompare(a.cr446_paymentpk)
        );
        setPayments(sortedPay);

        const ntJs = await nt.json();
        const noteArr = Array.isArray(ntJs) ? ntJs : ntJs.value || [];
        const sortedNotes = [...noteArr].sort(
          (a, b) =>
            new Date(b.modifiedon || b.createdon) -
            new Date(a.modifiedon || a.createdon)
        );
        setNotes(sortedNotes);

        document.title = `${legJs.cj_name} • Legal Detail`;
      } catch {
        setError("Could not load legal case.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const [newNoteFile, setNewNoteFile] = useState(null);

  /* ── NEW: function to POST a new timeline note ── */
  const handleAddTimelineNote = async () => {
    if (!newTimelineNote.trim() && !newNoteFile) return;

    setPostingNote(true);
    const token = localStorage.getItem("token");

    try {
      const fd = new FormData();
      fd.append("notetext", newTimelineNote.trim());

      if (newNoteFile) {
        fd.append("file", newNoteFile);
      }

      const res = await fetch(
        `https://portal.gmacc.co.za/api/legal/${id}/timeline-notes/create/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`, // ❗ NO Content-Type
          },
          body: fd,
        }
      );

      if (!res.ok) throw new Error("Add note failed");

      // Refresh timeline
      const nt = await fetch(
        `https://portal.gmacc.co.za/api/legals/${id}/timeline/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (nt.ok) {
        const js = await nt.json();
        const arr = Array.isArray(js) ? js : js.value || [];
        const sorted = [...arr].sort(
          (a, b) =>
            new Date(b.modifiedon || b.createdon) -
            new Date(a.modifiedon || a.createdon)
        );
        setNotes(sorted);
      }

      // Reset UI
      setNewTimelineNote("");
      setNewNoteFile(null);
      const fileEl = document.getElementById("timeline-note-file");
      if (fileEl) fileEl.value = "";
    } catch (err) {
      console.error(err);
      alert("Failed to add note.");
    } finally {
      setPostingNote(false);
    }
  };

  /* ────────── tabs / pages ────────── */
  const tabs = ["General", "Payments", "Timeline"];
  const [activeTab, setActiveTab] = useState("General");
  const perPage = 10;
  const [payPage, setPayPage] = useState(1);
  const [notePage, setNotePage] = useState(1);

  if (loading)
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );

  /* ────────── config for cards ────────── */
  const summaryFields = [
    ["Case Name", "cj_name"],
    ["Progress", "cj_progress"],
    ["Next Follow Up", "cj_nextfollowupdate"],
  ];
  const relationship = [
    ["Attorney", "_cj_attorney_value"],
    ["Attorney Ref.", "cr446_attorneyaccountreference"],
    ["Account", "_cj_account_value"],
    ["Handover Date", "cj_handoverdate"],
    ["Debt on Handover", "cj_debtonhandover"],
  ];
  const caseEvents = [
    ["Letter of Demand", "cr446_letterofdemand"],
    ["Summons Sent To Court", "cr446_summonssenttocourt"],
    ["Summons Served", "cj_summonsserved"],
    ["Warrant of Execution", "cr446_warrantofexecution"],
    ["Judgement", "cr446_judgement"],
    ["Judgement Granted", "cj_judgementgranted"],
    ["Warrant of Execution", "cr446_warrantofexecution"],
    ["Section 65", "cj_section65"],
    ["Section 66", "cj_section66"],
  ];
  const aodInfo = [
    ["AOD Start Date", "cj_attorneyaodstartdate"],
    ["AOD End Date", "cj_aodenddate"],
    ["AOD Amount", "cj_aodamount"],
  ];
  const hasAod = aodInfo.some(([_, k]) => legal?.[k]);
  /* ────────── ui ────────── */
  return (
    <div className={styles.page}>
      {/* ═══ NAVBAR ═══ */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            {buildingname} - {legal.cj_name}
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      <div className={styles.shell}>
        <PortalSidebar
  activePortal="trustee"
  activeTab="buildings"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole="trustee"
/>
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {/* ═══ SUMMARY ═══ */}

            <div className={styles.paymentHeaderCard}>
              {/* BACK BUTTON */}
              <button
                className={styles.paymentBackBtn}
                onClick={() => navigate(-1)}
              >
                Back
              </button>
              {/* TITLE */}
              <h1 className={styles.paymentHeaderTitle}>LEGAL CASE</h1>

              {/* DIVIDER */}
              <div className={styles.paymentHeaderDivider} />

              {/* LABEL ROW — 3-COLUMN HEADER GRID */}
              <div className={styles.paymentHeaderGrid3}>
                {summaryFields.map(([label]) => (
                  <div key={label} className={styles.paymentHeaderLabelItem}>
                    {label}:
                  </div>
                ))}
              </div>

              {/* VALUE PILL */}
              <div className={styles.paymentHeaderValuePill}>
                <div className={styles.paymentHeaderGrid3}>
                  {summaryFields.map(([_, key]) => (
                    <div key={key} className={styles.paymentHeaderValueItem}>
                      {key.toLowerCase().includes("date")
                        ? fmtDate(legal[key])
                        : display(legal, key)}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ═══ TABS ═══ */}
            <div className={styles.subTabs}>
              {tabs.map((t) => (
                <div
                  key={t}
                  className={
                    activeTab === t ? styles.subTabActive : styles.subTab
                  }
                  onClick={() => {
                    setActiveTab(t);
                    setPayPage(1);
                    setNotePage(1);
                  }}
                >
                  {t}
                </div>
              ))}
            </div>

            {/* ═══ TAB CONTENT ═══ */}
            <div className={styles.paymentCardGrid}>
              {/* ── GENERAL ── */}
              {activeTab === "General" && (
                <>
                  {/* Relationship / links */}
                  {/* Relationship / links */}
                  <div className={styles.paymentCardDark}>
                    <h2 className={styles.cardSectionTitle}>General</h2>

                    {relationship.map(([lbl, k]) => (
                      <div key={k} className={styles.inputGroup}>
                        <h3 className={styles.inputLabel}>{lbl}:</h3>

                        {["_cj_account_value", "_cj_attorney_value"].includes(
                          k
                        ) ? (
                          <span
                            className={`${styles.inputField} ${styles.relatedLinkDark}`}
                            onClick={() =>
                              navigate(
                                k === "_cj_account_value"
                                  ? `/levyaccount/${legal[k]}/${buildingId}`
                                  : `/contractor/${legal[k]}/${buildingId}`
                              )
                            }
                          >
                            {display(legal, k)}
                          </span>
                        ) : (
                          <input
                            readOnly
                            className={styles.inputField}
                            value={
                              k.includes("date")
                                ? fmtDate(legal[k])
                                : display(legal, k)
                            }
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Case events */}
                  <div className={styles.paymentCard}>
                    <h2>Case Events</h2>
                    {caseEvents.map(([lbl, k]) => (
                      <div key={k} className={styles["input-group"]}>
                        <h3>{lbl}:</h3>
                        <input
                          readOnly
                          value={
                            k.includes("date")
                              ? fmtDate(legal[k])
                              : display(legal, k)
                          }
                        />
                      </div>
                    ))}
                  </div>

                  {/* Attorney AOD (read-only) */}
                  {(aodInfo.some(([_, k]) => legal?.[k]) ||
                    aodMeta.exists ||
                    legal?.cr446_activeattorneyaod !== undefined) && (
                    <div className={styles.paymentCard}>
                      <h2>Attorney AOD Information</h2>

                      {/* Active Attorney AOD */}
                      <div className={styles["input-group"]}>
                        <h3>Active Attorney AOD:</h3>
                        <input
                          readOnly
                          value={
                            legal?.[
                              "cr446_activeattorneyaod@OData.Community.Display.V1.FormattedValue"
                            ] ??
                            (typeof legal?.cr446_activeattorneyaod === "boolean"
                              ? legal.cr446_activeattorneyaod
                                ? "Yes"
                                : "No"
                              : "—")
                          }
                        />
                      </div>

                      {/* Attorney AOD File – download-only */}
                      <div className={styles["input-group"]}>
                        <h3>Attorney AOD File:</h3>
                        {legal?.["cr446_attorneyaodfile@odata.mediaReadLink"] ||
                        aodMeta.exists ? (
                          <button
                            aria-label="Download Attorney AOD file"
                            onClick={() => {
                              const url = `https://portal.gmacc.co.za/api/legal/${id}/download/aod-file/`;
                              const a = document.createElement("a");
                              a.href = url;
                              if (aodMeta?.file_name)
                                a.download = aodMeta.file_name; // optional
                              document.body.appendChild(a);
                              a.click();
                              a.remove();
                            }}
                          >
                            Download
                          </button>
                        ) : (
                          <span className={styles.muted}>No file uploaded</span>
                        )}
                      </div>

                      {/* Existing read-only AOD fields */}
                      {aodInfo.map(([lbl, k]) => (
                        <div key={k} className={styles["input-group"]}>
                          <h3>{lbl}:</h3>
                          <input
                            readOnly
                            value={
                              k.toLowerCase().includes("date")
                                ? fmtDate(legal[k])
                                : legal?.[
                                    `${k}@OData.Community.Display.V1.FormattedValue`
                                  ] ??
                                  legal?.[k] ??
                                  "—"
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Latest note */}
                  <div className={styles.paymentCard}>
                    <h2>Current Situation</h2>
                    <p className={styles.infoText}>
                      {legal.cj_currentsituationdescription ?? "—"}
                    </p>
                  </div>
                </>
              )}

              {/* ── PAYMENTS ── */}
              {activeTab === "Payments" && (
                <div className={styles.paymentCard}>
                  <h2>Payments</h2>
                  <div className={styles.responsiveTableWrapper}>
                    <table className={styles.dataTable}>
                      <thead>
                        <tr>
                          <th>Payment Number</th>
                          <th>Amount</th>
                          <th>Contractor</th>
                          <th>Status</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {payments
                          .slice((payPage - 1) * perPage, payPage * perPage)
                          .map((p) => (
                            <tr
                              key={p.cr446_paymentid}
                              onClick={() =>
                                navigate(
                                  `/payment/${p.cr446_paymentid}/${buildingId}`
                                )
                              }
                              style={{ cursor: "pointer" }}
                            >
                              <td className={styles.paymentNumberCell}>
                                {p.cr446_paymentpk}
                              </td>
                              <td className={styles.amountCell}>
                                <span className={styles.amountValue}>
                                  {formatAmount(p.cr446_amount)}
                                </span>
                              </td>
                              <td>
                                {
                                  p[
                                    "_cr446_contractorname_value@OData.Community.Display.V1.FormattedValue"
                                  ]
                                }
                              </td>
                              <td>
                                {p[
                                  "cj_progress@OData.Community.Display.V1.FormattedValue"
                                ] || p.cj_progress}
                              </td>
                              <td>
                                {fmtDate(p.cj_paymenttime || p.createdon)}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <Pagination
                    page={payPage}
                    total={payments.length}
                    perPage={perPage}
                    onChange={setPayPage}
                  />
                </div>
              )}

              {/* ── TIMELINE NOTES ── */}
              {activeTab === "Timeline" && (
                <div className={styles.paymentCard}>
                  <h2>Current Situation</h2>
                  <p className={styles.infoText}>
                    {legal.cj_currentsituationdescription ?? "—"}
                  </p>

                  <h2>Timeline</h2>

                  {notes.length === 0 ? (
                    <p className={styles.infoText}>No notes available.</p>
                  ) : (
                    <>
                      <div className={styles.responsiveTableWrapper}>
                        <table className={styles.dataTable}>
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Note</th>
                              <th>Created By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {notes
                              .slice(
                                (notePage - 1) * perPage,
                                notePage * perPage
                              )
                              .map((n) => (
                                <tr key={n.annotationid}>
                                  <td>
                                    {fmtDate(n.modifiedon || n.createdon)}
                                  </td>
                                  <td>
                                    <div
                                      className={styles.htmlContent}
                                      dangerouslySetInnerHTML={{
                                        __html: transformNoteHtml(n.notetext),
                                      }}
                                    />
                                  </td>
                                  <td>
                                    {
                                      n[
                                        "_createdby_value@OData.Community.Display.V1.FormattedValue"
                                      ]
                                    }
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <Pagination
                        page={notePage}
                        total={notes.length}
                        perPage={perPage}
                        onChange={setNotePage}
                      />
                    </>
                  )}

                  {/* ── ADD NEW TIMELINE NOTE ── */}
                  <div className={styles.commentSection}>
                    <textarea
                      className={styles.commentInput}
                      placeholder="Add a timeline note..."
                      value={newTimelineNote}
                      onChange={(e) => setNewTimelineNote(e.target.value)}
                    />

                    <input
                      id="timeline-note-file"
                      type="file"
                      className={styles.editableInput}
                      onChange={(e) =>
                        setNewNoteFile(e.target.files?.[0] || null)
                      }
                      style={{ marginTop: "10px" }}
                    />

                    <button
                      className={styles.commentButton}
                      onClick={handleAddTimelineNote}
                      disabled={
                        postingNote || (!newTimelineNote.trim() && !newNoteFile)
                      }
                    >
                      {postingNote ? "Adding…" : "Add Note"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
